'use client';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Image from 'next/image';

import { useIdPath } from '@/contexts/IdPathProvider';
import { useLanguage } from '@/contexts/I18nProvider';
import minimapServices from '@/templates/Heritage/utils/services/minimap';
import { useTranslations } from 'next-intl';
import { IconMinimap, IconX, IconMenu, IconBulletList, IconArrowDown, IconArrowHeadDown, IconCurrentPosition, IconCurrentLocation, IconArrowLeft } from "@/templates/Heritage/utils/icons";
import ListLocation, { LocationItem } from "./ListLocation";
import ToggleMapButton from "./ToggleMapButton";
import { KrpanoMapViewer, useKrpanoMap } from "@/components/generateMinimapXml";
import LocationDetail from "./LocationDetail";
import ListMap from "./ListMap";
import LoginDialog from './LoginDialog';
import { useKrpano } from "@/contexts/KrpanoProvider";
import { useScene } from "@/templates/Heritage/hooks/useScenes";
import { sceneServices } from '@/templates/Heritage/utils/services/scene';
import groupViewServices from '@/templates/Heritage/utils/services/groupView';
import { Plus, Minus, RotateCcw } from 'lucide-react';
import { useModules } from "@/contexts/ModuleProvider";
import { isSceneMatching, isLocationMatchingScene, hasLocationSceneTarget } from "@/utils/minimapMarkerDisplay";

interface MinimapPopupQTSCProps {
    show?: any;
    setShow?: React.Dispatch<React.SetStateAction<any>>;
    hideToggleButton?: boolean;
    externalOpenSignal?: number;
    lockedOnlyMode?: boolean;
    /** Called whenever the map popup opens (true) or closes (false). Used by parent to pause animations on iPhone. */
    onOpenChange?: (isOpen: boolean) => void;
}

const getEmbedUrl = (url: string): string => {
    if (!url) return '';
    if (url.startsWith('http')) {
        try {
            const parsed = new URL(url);
            if (parsed.pathname.includes('/map3d')) {
                return parsed.pathname + parsed.search;
            }
        } catch (e) {
            // ignore
        }
        return url;
    }
    if (url.startsWith('/')) {
        return url;
    }
    return `/map3d/?project=${url}`;
};

export default function MinimapPopupQTSC({ hideToggleButton = false, externalOpenSignal, lockedOnlyMode = false, onOpenChange }: MinimapPopupQTSCProps) {
    const t = useTranslations();
    const { idPath } = useIdPath();
    const { locale } = useLanguage();
    const { currentScene } = useKrpano();
    const { navigateScene } = useScene();

    let activeModules: any = {};
    try {
        const modulesCtx = useModules();
        if (modulesCtx) {
            activeModules = modulesCtx.activeModules;
        }
    } catch (e) {
        // Fallback if not wrapped in provider
    }
    const isMap3dEnabled = Boolean(activeModules && activeModules['map3d']);
    const isMinimap2dEnabled = Boolean(
        !activeModules ||
        Object.keys(activeModules).length === 0 ||
        activeModules['minimapPopupQTSC'] ||
        activeModules['minimapHomescreen'] ||
        activeModules['minimap']
    );
    // Project-level auth stored in local state to avoid NextAuth's
    // signIn/getSession flow which crashes iOS Safari due to ITP
    // cookie race conditions.
    const [projectAuth, setProjectAuth] = useState<{
        accessToken: string;
        id_path: string;
        user: any;
    } | null>(null);
    const projectAuthStorageKey = `minimap_project_auth_${idPath}`;
    const isIPhoneSafari = typeof navigator !== 'undefined'
        && /iPhone/i.test(navigator.userAgent || '')
        && /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '');
    // const { locations } = useKrpanoMap();
    // UI State
    const [isClosing, setIsClosing] = useState(false);
    const [showMapPopup, setShowMapPopup] = useState(false);
    const [openListMarkers, setOpenListMarkers] = useState(false);


    // Map Data State
    const [mapSelected, setMapSelected] = useState<any>({});
    const [minimapData, setMinimapData] = useState<any>(null);
    const [loadedMapKey, setLoadedMapKey] = useState<string | null>(null);
    const [mapLocations, setMapLocations] = useState<any[]>([]);
    const [hasCurrentSceneMarker, setHasCurrentSceneMarker] = useState(false);

    const [currentLocation, setCurrentLocation] = useState<any>(null);
    const [openLocationDetail, setOpenLocationDetail] = useState(false);
    const sceneDetailRequestRef = useRef(0);
    const [sceneDetailError, setSceneDetailError] = useState<string | null>(null);
    const [hoveredLocationId, setHoveredLocationId] = useState<string | number | null>(null);
    const [selectedLocationId, setSelectedLocationId] = useState<string | number | null>(null);
    const [showLoginDialog, setShowLoginDialog] = useState(false);
    const [pendingMap, setPendingMap] = useState<any>(null);
    const [image2dPreview, setImage2dPreview] = useState<{ open: boolean; urls: string[]; activeUrl: string; title: string }>({
        open: false,
        urls: [],
        activeUrl: '',
        title: '',
    });
    const [previewScale, setPreviewScale] = useState(1);
    const [previewTranslate, setPreviewTranslate] = useState({ x: 0, y: 0 });
    const previewViewportRef = useRef<HTMLDivElement | null>(null);
    const previewDragRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
    const previewPinchDistanceRef = useRef<number | null>(null);
    const sceneGroupIdsCacheRef = useRef<Map<string, string[]>>(new Map());

    const clampPreviewScale = useCallback((nextScale: number) => {
        return Math.min(4, Math.max(1, nextScale));
    }, []);

    const clampPreviewTranslate = useCallback((x: number, y: number, scale: number) => {
        const viewport = previewViewportRef.current;
        if (!viewport || scale <= 1) {
            return { x: 0, y: 0 };
        }

        const maxX = ((scale - 1) * viewport.clientWidth) / 2;
        const maxY = ((scale - 1) * viewport.clientHeight) / 2;

        return {
            x: Math.min(maxX, Math.max(-maxX, x)),
            y: Math.min(maxY, Math.max(-maxY, y)),
        };
    }, []);

    const resetPreviewTransform = useCallback(() => {
        setPreviewScale(1);
        setPreviewTranslate({ x: 0, y: 0 });
        previewDragRef.current.active = false;
        previewPinchDistanceRef.current = null;
    }, []);

    const zoomInPreview = useCallback(() => {
        setPreviewScale((prev) => clampPreviewScale(prev + 0.25));
    }, [clampPreviewScale]);

    const zoomOutPreview = useCallback(() => {
        setPreviewScale((prev) => clampPreviewScale(prev - 0.25));
    }, [clampPreviewScale]);

    const startPreviewDrag = useCallback((clientX: number, clientY: number) => {
        if (previewScale <= 1) return;

        previewDragRef.current = {
            active: true,
            startX: clientX,
            startY: clientY,
            originX: previewTranslate.x,
            originY: previewTranslate.y,
        };
    }, [previewScale, previewTranslate.x, previewTranslate.y]);

    const movePreviewDrag = useCallback((clientX: number, clientY: number) => {
        if (!previewDragRef.current.active) return;

        const deltaX = clientX - previewDragRef.current.startX;
        const deltaY = clientY - previewDragRef.current.startY;
        const next = clampPreviewTranslate(
            previewDragRef.current.originX + deltaX,
            previewDragRef.current.originY + deltaY,
            previewScale,
        );

        setPreviewTranslate(next);
    }, [clampPreviewTranslate, previewScale]);

    const stopPreviewDrag = useCallback(() => {
        previewDragRef.current.active = false;
    }, []);

    const handlePreviewWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
        event.preventDefault();

        const delta = event.deltaY < 0 ? 0.15 : -0.15;
        setPreviewScale((prev) => clampPreviewScale(prev + delta));
    }, [clampPreviewScale]);

    const getTouchDistance = useCallback((
        firstTouch: { clientX: number; clientY: number },
        secondTouch: { clientX: number; clientY: number },
    ) => {
        const distanceX = secondTouch.clientX - firstTouch.clientX;
        const distanceY = secondTouch.clientY - firstTouch.clientY;
        return Math.hypot(distanceX, distanceY);
    }, []);

    useEffect(() => {
        setPreviewTranslate((prev) => clampPreviewTranslate(prev.x, prev.y, previewScale));
    }, [previewScale, clampPreviewTranslate]);

    useEffect(() => {
        if (!image2dPreview.open) return;
        resetPreviewTransform();
    }, [image2dPreview.open, image2dPreview.activeUrl, resetPreviewTransform]);

    useEffect(() => {
        const handleToggleMinimap = (e: CustomEvent) => {
            const nextShow = (e.detail && typeof e.detail.show === 'boolean') ? e.detail.show : !showMapPopup;
            if (nextShow) {
                sceneDetailRequestRef.current += 1;
                setSelectedLocationId(null);
                setOpenLocationDetail(false);
                setCurrentLocation(null);
            }
            setShowMapPopup(nextShow);
        };
        window.addEventListener('toggle_qtsc_minimap', handleToggleMinimap as EventListener);
        return () => window.removeEventListener('toggle_qtsc_minimap', handleToggleMinimap as EventListener);
    }, [showMapPopup]);

    const dedupeImageUrls = useCallback((values: string[]) => {
        const seen = new Set<string>();
        const next: string[] = [];

        values.forEach((value) => {
            const normalized = String(value || '').trim();
            if (!normalized || seen.has(normalized)) return;
            seen.add(normalized);
            next.push(normalized);
        });

        return next;
    }, []);

    const isLikelyImageUrl = useCallback((value: unknown) => {
        const normalized = String(value || '').trim();
        if (!normalized) return false;

        if (
            normalized.startsWith('http://')
            || normalized.startsWith('https://')
            || normalized.startsWith('//')
            || normalized.startsWith('/upload/')
            || normalized.startsWith('upload/')
            || normalized.startsWith('data:image/')
        ) {
            return true;
        }

        return /\.(png|jpe?g|webp|gif|svg|bmp|avif)(\?|#|$)/i.test(normalized);
    }, []);

    const extractImageUrlsDeep = useCallback((value: unknown): string[] => {
        if (value === null || value === undefined) return [];

        if (Array.isArray(value)) {
            return value.flatMap((item) => extractImageUrlsDeep(item));
        }

        if (typeof value === 'object') {
            return Object.values(value as Record<string, unknown>)
                .flatMap((item) => extractImageUrlsDeep(item));
        }

        if (typeof value !== 'string') return [];

        const trimmed = value.trim();
        if (!trimmed) return [];

        if (
            (trimmed.startsWith('{') && trimmed.endsWith('}'))
            || (trimmed.startsWith('[') && trimmed.endsWith(']'))
        ) {
            try {
                return extractImageUrlsDeep(JSON.parse(trimmed));
            } catch {
                // Fall through to text split.
            }
        }

        return trimmed
            .split(/\r?\n|,|;/)
            .map((item) => item.trim())
            .filter((item) => isLikelyImageUrl(item));
    }, [isLikelyImageUrl]);

    const parseMarkerConfigObject = useCallback((rawConfig: unknown): Record<string, unknown> => {
        if (!rawConfig) return {};

        if (typeof rawConfig === 'string') {
            try {
                const parsed = JSON.parse(rawConfig);
                return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                    ? parsed as Record<string, unknown>
                    : {};
            } catch {
                return {};
            }
        }

        if (typeof rawConfig === 'object' && !Array.isArray(rawConfig)) {
            return rawConfig as Record<string, unknown>;
        }

        return {};
    }, []);

    const getSceneGroupIdsBySceneName = useCallback(async (sceneName: string): Promise<string[]> => {
        const normalizedSceneName = String(sceneName || '').trim();
        if (!normalizedSceneName || !idPath) {
            return [];
        }

        const cacheKey = `${idPath}:${locale}:${normalizedSceneName}`;
        const cached = sceneGroupIdsCacheRef.current.get(cacheKey);
        if (cached) {
            return cached;
        }

        try {
            const response = await groupViewServices.getGroupOfCurrentScene({
                id_path: idPath,
                language: locale,
                scene_id: normalizedSceneName,
            });

            const groups = (response && typeof response === 'object' && Array.isArray((response as { data?: unknown }).data))
                ? ((response as { data?: unknown[] }).data || [])
                : [];
            const groupIds = groups
                .map((group) => {
                    if (!group || typeof group !== 'object') return '';
                    const groupRecord = group as Record<string, unknown>;
                    return String(groupRecord.id ?? groupRecord.group_id ?? '').trim();
                })
                .filter(Boolean);

            sceneGroupIdsCacheRef.current.set(cacheKey, groupIds);
            return groupIds;
        } catch {
            sceneGroupIdsCacheRef.current.set(cacheKey, []);
            return [];
        }
    }, [idPath, locale]);

    useEffect(() => {
        sceneGroupIdsCacheRef.current.clear();
    }, [idPath, locale]);

    const openImage2dPreview = useCallback((urls: string[], title: string) => {
        const normalizedUrls = dedupeImageUrls(urls);
        if (normalizedUrls.length === 0) {
            return;
        }

        setImage2dPreview({
            open: true,
            urls: normalizedUrls,
            activeUrl: normalizedUrls[0],
            title: title || 'Hinh anh 2D',
        });
    }, [dedupeImageUrls]);

    /**
     * Preload map images (especially locked maps) early — during idle time —
     * so the browser has already decoded the bitmap by the time the popup opens.
     * This avoids the memory spike that causes iOS WebKit to kill the process
     * right as the minimap appears after login.
     */
    const preloadLockedMapImages = useCallback((maps: any[]) => {
        if (typeof window === 'undefined' || !Array.isArray(maps)) return;

        const apiBase = '/api/proxy';
        const { isIPhone, isIOSSafari, isAndroid } = (() => {
            const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
            const isIPhone = /iPhone/i.test(ua);
            const isIOS = /iP(hone|od|ad)/i.test(ua);
            const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
            const isAndroid = /android/i.test(ua);
            return { isIPhone, isIOSSafari: isIOS && isSafari, isAndroid };
        })();

        const targetWidth = isIPhone ? 1600 : isIOSSafari ? 1600 : isAndroid ? 1600 : 2048;
        const quality = isIPhone ? 75 : isIOSSafari ? 72 : isAndroid ? 75 : 80;

        maps.forEach((map: any) => {
            // Prioritise locked maps since they are the ones that get loaded right after login
            const rawUrl: string | undefined = map?.url_map || map?.translations?.[0]?.url_map;
            if (!rawUrl || rawUrl.includes('{z}') || rawUrl.includes('{x}')) return;

            const absoluteUrl = rawUrl.startsWith('http')
                ? rawUrl
                : `${window.location.origin}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;

            const proxyUrl = apiBase
                ? `${apiBase}/v1/general/cmc/proxy-image?fileUrl=${encodeURIComponent(absoluteUrl)}&w=${targetWidth}&q=${quality}`
                : absoluteUrl;

            const doPreload = () => {
                const img = new window.Image();
                img.decoding = 'async';
                img.src = proxyUrl;
                img.decode?.().catch(() => { /* best-effort */ });
            };

            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(doPreload, { timeout: 4000 });
            } else {
                setTimeout(doPreload, 1000);
            }
        });
    }, []);
    const normalizeMinimapList = useCallback((raw: any): any[] => {
        if (!raw) return [];

        if (Array.isArray(raw)) {
            return raw;
        }

        if (Array.isArray(raw?.data)) {
            return raw.data;
        }

        if (Array.isArray(raw?.data?.data)) {
            return raw.data.data;
        }

        return [];
    }, []);
    const mapControlsRef = useRef<{
        zoomIn: () => void;
        zoomOut: () => void;
        resetZoom: () => void;
    } | null>(null);
    // Ref for the overlay element to attach non-passive touch listeners
    const overlayRef = useRef<HTMLDivElement>(null);
    // Tracks whether the initial map auto-selection has run for the
    // current minimapData.  Prevents the useEffect from overriding a
    // user/login-driven selection when canAccessMap changes.
    const initialMapSelectedRef = useRef(false);
    const lastExternalOpenSignalRef = useRef<number | undefined>(undefined);

    const displayMaps = useMemo(() => {
        if (!Array.isArray(minimapData)) {
            return [];
        }

        // Older records may omit visibility; explicit zero always stays private.
        let filtered = minimapData.filter((map: any) => map?.is_public == null || Number(map.is_public) !== 0);

        // Filter based on active modules
        filtered = filtered.filter((m: any) => {
            const mapType = m?.type || '2d';
            if (mapType === '3d') {
                return isMap3dEnabled;
            }
            return isMinimap2dEnabled;
        });

        if (lockedOnlyMode) {
            return filtered.filter((m: any) => Number(m?.require_login) === 1);
        }

        return filtered;
    }, [minimapData, lockedOnlyMode, isMap3dEnabled, isMinimap2dEnabled]);

    const selectedMapTitle = loadedMapKey === `${idPath}:${locale}`
        ? displayMaps.find((map: any) => String(map?.id) === String(mapSelected?.id))?.title
        : null;

    const getPreferredMap = useCallback((maps: any[]) => {
        if (!Array.isArray(maps) || maps.length === 0) {
            return null;
        }

        if (lockedOnlyMode) {
            return maps.find((m: any) => Number(m?.main_map) === 1) || maps[0];
        }

        const preferredMap = maps.find((m: any) => m.main_map == 1) || maps[0];
        const isPreferredLocked = Number(preferredMap?.require_login) === 1;
        if (!isPreferredLocked) {
            return preferredMap;
        }

        return maps.find((m: any) => Number(m?.require_login) !== 1) || preferredMap;
    }, [lockedOnlyMode]);

    const getJwtExpMs = useCallback((token?: string) => {
        if (!token || token.split('.').length < 2) {
            return null;
        }

        try {
            const payloadBase64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const payload = JSON.parse(atob(payloadBase64));
            if (!payload?.exp || Number.isNaN(Number(payload.exp))) {
                return null;
            }
            return Number(payload.exp) * 1000;
        } catch {
            return null;
        }
    }, []);

    const isTokenExpired = useCallback((token?: string) => {
        const expMs = getJwtExpMs(token);
        if (!expMs) return false;
        return Date.now() >= expMs;
    }, [getJwtExpMs]);

    const clearProjectAuthSession = useCallback(() => {
        setProjectAuth(null);
        if (typeof window !== 'undefined') {
            try {
                window.sessionStorage.removeItem(projectAuthStorageKey);
            } catch {
                // Ignore storage cleanup errors.
            }

            window.dispatchEvent(new CustomEvent('minimap-auth-sync', {
                detail: { key: projectAuthStorageKey, auth: null }
            }));
        }
    }, [projectAuthStorageKey]);

    const isProjectAuthValid = useCallback((authData: { accessToken: string; id_path: string; user: any } | null) => {
        if (!authData?.accessToken || !idPath) return false;
        if (String(authData.id_path) !== String(idPath)) return false;
        return !isTokenExpired(authData.accessToken);
    }, [idPath, isTokenExpired]);

    const resolveExistingMap = useCallback((candidate: any, maps: any[]) => {
        if (!Array.isArray(maps) || maps.length === 0) {
            return null;
        }

        if (candidate?.id !== undefined && candidate?.id !== null) {
            const found = maps.find((m: any) => Number(m?.id) === Number(candidate.id));
            if (found) {
                return found;
            }
        }

        return getPreferredMap(maps);
    }, [getPreferredMap]);

    const hasValidProjectSession = useCallback(() => {
        // Check local project auth first (no cookies/NextAuth involved)
        return isProjectAuthValid(projectAuth);
    }, [projectAuth, isProjectAuthValid]);

    useEffect(() => {
        if (!idPath || typeof window === 'undefined') {
            return;
        }

        try {
            const navEntries = window.performance?.getEntriesByType?.('navigation') as PerformanceNavigationTiming[];
            const isReloadNavigation = navEntries?.[0]?.type === 'reload'
                || ((window.performance as any)?.navigation?.type === 1);

            // Business rule: after F5/reload, locked minimap must require login again.
            if (isReloadNavigation) {
                window.sessionStorage.removeItem(projectAuthStorageKey);
                setProjectAuth(null);
                return;
            }

            const raw = window.sessionStorage.getItem(projectAuthStorageKey);
            if (!raw) {
                return;
            }

            const stored = JSON.parse(raw);
            if (isProjectAuthValid(stored)) {
                setProjectAuth(stored);
            } else {
                window.sessionStorage.removeItem(projectAuthStorageKey);
                setProjectAuth(null);
            }
        } catch (error) {
            console.warn('Failed to restore minimap project auth from sessionStorage:', error);
        }
    }, [idPath, projectAuthStorageKey, isProjectAuthValid]);

    useEffect(() => {
        if (!idPath || typeof window === 'undefined') {
            return;
        }

        try {
            if (isProjectAuthValid(projectAuth)) {
                window.sessionStorage.setItem(projectAuthStorageKey, JSON.stringify(projectAuth));
            } else {
                window.sessionStorage.removeItem(projectAuthStorageKey);
            }
        } catch (error) {
            console.warn('Failed to persist minimap project auth to sessionStorage:', error);
        }
    }, [projectAuth, idPath, projectAuthStorageKey, isProjectAuthValid]);

    // Listen for auth changes from other MinimapPopupQTSC instances (same tab)
    // This enables auth sharing between the locked minimap button and map popup
    useEffect(() => {
        if (typeof window === 'undefined' || !idPath) return;

        const handleAuthSync = (e: CustomEvent) => {
            if (e.detail?.key !== projectAuthStorageKey) {
                return;
            }

            if (e.detail?.auth) {
                setProjectAuth(e.detail.auth);
            } else {
                setProjectAuth(null);
            }
        };

        window.addEventListener('minimap-auth-sync', handleAuthSync as EventListener);
        return () => {
            window.removeEventListener('minimap-auth-sync', handleAuthSync as EventListener);
        };
    }, [idPath, projectAuthStorageKey]);


    const requestMapAccess = useCallback((map: any) => {
        const isLoginRequired = Number(map?.require_login) === 1;
        if (isLoginRequired && !hasValidProjectSession()) {
            if (projectAuth?.accessToken) {
                clearProjectAuthSession();
            }
            // Keep popup mounted and show login as overlay.
            // Avoiding close/reopen prevents expensive KRPano remounts on iPhone.
            setPendingMap(map);
            setShowLoginDialog(true);
            return false;
        }
        return true;
    }, [hasValidProjectSession, projectAuth?.accessToken, clearProjectAuthSession]);

    // If user leaves tab idle and token expires, invalidate lock-map auth and
    // require login again before showing private minimap data.
    useEffect(() => {
        if (!projectAuth?.accessToken) {
            return;
        }

        const checkExpiry = () => {
            if (isProjectAuthValid(projectAuth)) {
                return;
            }

            const lockedMapVisible = Number(mapSelected?.require_login) === 1;
            clearProjectAuthSession();

            if (lockedMapVisible) {
                setPendingMap(mapSelected);
                setShowLoginDialog(true);
            }
        };

        checkExpiry();
        const intervalId = window.setInterval(checkExpiry, 30000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [projectAuth, mapSelected, isProjectAuthValid, clearProjectAuthSession]);

    // Handle showing the map popup
    const handleShowMapPopup = useCallback(() => {
        if (showLoginDialog) {
            return;
        }

        const resolvedMap = resolveExistingMap(mapSelected, displayMaps);
        if (!resolvedMap) {
            return;
        }

        if (Number(mapSelected?.id) !== Number(resolvedMap.id)) {
            setMapSelected(resolvedMap);
        }

        if (!requestMapAccess(resolvedMap)) {
            return;
        }

        sceneDetailRequestRef.current += 1;
        setSelectedLocationId(null);
        setOpenLocationDetail(false);
        setCurrentLocation(null);

        setShowMapPopup(true);
    }, [mapSelected, displayMaps, requestMapAccess, showLoginDialog, resolveExistingMap]);

    // Handle closing the overlay with animation
    const closeOverlay = useCallback(() => {
        sceneDetailRequestRef.current += 1;
        setIsClosing(true);
        setSelectedLocationId(null);
        setOpenLocationDetail(false);
        setCurrentLocation(null);
        onOpenChange?.(false);
        setTimeout(() => {
            setShowMapPopup(false);
            setImage2dPreview({ open: false, urls: [], activeUrl: '', title: '' });
            setIsClosing(false);
            setMapLocations([]);
        }, 300);
    }, [onOpenChange]);

    // Handle marker click - close the popup
    const handleMarkerClick = useCallback(() => {
        closeOverlay();
    }, [closeOverlay]);

    // Handle when locations are loaded from the map
    const handleLocationsLoaded = useCallback((locations: any[]) => {
        setMapLocations(locations);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const evaluateCurrentSceneMarker = async () => {
            if (!showMapPopup || !currentScene || !Array.isArray(mapLocations) || mapLocations.length === 0) {
                if (!cancelled) setHasCurrentSceneMarker(false);
                return;
            }

            const directMatched = mapLocations.some((item) => (item.type === 'scene' || item.type === 'view') && isLocationMatchingScene(item, currentScene));
            if (directMatched) {
                if (!cancelled) setHasCurrentSceneMarker(true);
                return;
            }

            const groupLinkedMarkers = mapLocations.filter((item) => {
                if (item.type !== 'scene') return false;
                if (hasLocationSceneTarget(item)) return false;
                const markerConfig = parseMarkerConfigObject(item.config);
                return markerConfig.scene_link_type === 'group_scene';
            });

            if (groupLinkedMarkers.length === 0) {
                if (!cancelled) setHasCurrentSceneMarker(false);
                return;
            }

            const currentSceneGroupIds = await getSceneGroupIdsBySceneName(currentScene);
            const currentGroupSet = new Set(currentSceneGroupIds);

            if (currentGroupSet.size === 0) {
                if (!cancelled) setHasCurrentSceneMarker(false);
                return;
            }

            for (const marker of groupLinkedMarkers) {
                const markerConfig = parseMarkerConfigObject(marker.config);
                const configGroupId = String(markerConfig.scene_group_id || '').trim();

                if (configGroupId && currentGroupSet.has(configGroupId)) {
                    if (!cancelled) setHasCurrentSceneMarker(true);
                    return;
                }

                const markerSceneRef = String(marker.ref || '').trim();
                if (!markerSceneRef) {
                    continue;
                }

                const markerSceneGroupIds = await getSceneGroupIdsBySceneName(markerSceneRef);
                const isSameGroup = markerSceneGroupIds.some((groupId) => currentGroupSet.has(groupId));

                if (isSameGroup) {
                    if (!cancelled) setHasCurrentSceneMarker(true);
                    return;
                }
            }

            if (!cancelled) setHasCurrentSceneMarker(false);
        };

        evaluateCurrentSceneMarker();

        return () => {
            cancelled = true;
        };
    }, [
        currentScene,
        mapLocations,
        showMapPopup,
        parseMarkerConfigObject,
        getSceneGroupIdsBySceneName,
    ]);

    // Handle map ready callback
    const handleMapReady = useCallback((_krpano: unknown) => {
        // Map is ready
    }, []);

    // Auto-select the initial map ONCE when minimapData first arrives.
    // We intentionally do NOT re-run this when canAccessMap changes
    // (i.e. after login), because that would override the user's
    // pending-map selection from handleLoginSuccess with the
    // preferred/public map, causing the locked map to never load.
    useEffect(() => {
        if (displayMaps && displayMaps.length > 0 && !initialMapSelectedRef.current) {
            initialMapSelectedRef.current = true;
            const preferredMap = getPreferredMap(displayMaps);
            if (preferredMap) {
                setMapSelected(preferredMap);
            }
        }
    }, [displayMaps, getPreferredMap]);

    // Heal stale selected map IDs (e.g., after project/context changes) and
    // synchronize mapSelected with the newly translated map object when locale changes.
    useEffect(() => {
        if (!Array.isArray(displayMaps) || displayMaps.length === 0) {
            if (loadedMapKey !== `${idPath}:${locale}`) return;
            setMapSelected((selected: any) => selected?.id == null ? selected : {});
            setPendingMap(null);
            setShowLoginDialog(false);
            setShowMapPopup(false);
            initialMapSelectedRef.current = false;
            if (showMapPopup) onOpenChange?.(false);
            return;
        }

        if (!mapSelected?.id) {
            return;
        }

        const matchedMap = displayMaps.find((m: any) => Number(m?.id) === Number(mapSelected.id));
        if (matchedMap) {
            if (matchedMap !== mapSelected) {
                setMapSelected(matchedMap);
            }
        } else {
            const fallback = getPreferredMap(displayMaps);
            if (fallback) {
                setMapSelected(fallback);
            }
        }
    }, [displayMaps, mapSelected, getPreferredMap, showMapPopup, onOpenChange, loadedMapKey, idPath, locale]);

    // Refresh open location detail when language (locale) changes
    useEffect(() => {
        if (!openLocationDetail || !currentLocation || !idPath) {
            return;
        }

        const sceneTarget = currentLocation.entity_id || currentLocation.id || currentLocation.name || currentLocation.scene_id;
        if (!sceneTarget) return;

        const reqId = ++sceneDetailRequestRef.current;
        sceneServices.getSceneById({
            id_path: idPath,
            language: locale,
            scene_id: String(sceneTarget),
            gender: "nu"
        }).then((response: any) => {
            if (reqId === sceneDetailRequestRef.current && response) {
                const updated = response?.data || response;
                if (updated && (updated.title || updated.name)) {
                    setCurrentLocation(updated);
                }
            }
        }).catch((err) => {
            console.warn('Failed to refresh location detail on language change:', err);
        });
    }, [locale, idPath, openLocationDetail]);

    // Reset first-selection guard when project changes, otherwise previous project's selected
    // map id can leak into current project and cause /minimap/:id 404.
    useEffect(() => {
        initialMapSelectedRef.current = false;
    }, [idPath]);

    // Refresh map titles when the project or language changes.
    useEffect(() => {
        let cancelled = false;
        const acceptMaps = (maps: any[]) => {
            if (cancelled) return;
            const publicMaps = maps.filter((map: any) => map?.is_public == null || Number(map.is_public) !== 0);
            setMinimapData(publicMaps);
            setLoadedMapKey(`${idPath}:${locale}`);
            if (publicMaps.length > 0) preloadLockedMapImages(publicMaps);
        };
        const fetchMap = async () => {
            try {
                const allMapsResponse: any = await minimapServices.getAllMinimaps(idPath, locale);
                if (cancelled) return;
                const allMaps = normalizeMinimapList(allMapsResponse);

                if (allMaps.length > 0) {
                    acceptMaps(allMaps);
                    return;
                }

                // Fallback path: if list endpoint returns empty/shape mismatch, try main-map endpoint.
                const mainMapResponse: any = await minimapServices.getMainMap(idPath, locale);
                if (cancelled) return;
                const mainMaps = normalizeMinimapList(mainMapResponse);

                if (mainMaps.length > 0) {
                    acceptMaps(mainMaps);
                    return;
                }

                acceptMaps([]);
            } catch (err) {
                if (!cancelled) {
                    console.error('Error fetching map data:', err);
                    acceptMaps([]);
                }
            }
        };
        if (idPath) fetchMap();
        return () => { cancelled = true; };
    }, [idPath, locale, normalizeMinimapList, preloadLockedMapImages]);

    // Open from external trigger (used by project-specific buttons in other UIs)
    useEffect(() => {
        if (externalOpenSignal === undefined) {
            return;
        }

        if (externalOpenSignal === lastExternalOpenSignalRef.current) {
            return;
        }

        lastExternalOpenSignalRef.current = externalOpenSignal;

        if (!displayMaps.length) {
            return;
        }

        const preferredMap = getPreferredMap(displayMaps);
        if (!preferredMap) {
            return;
        }

        const resolvedMap = resolveExistingMap(preferredMap, displayMaps);
        if (!resolvedMap) {
            return;
        }

        setMapSelected(resolvedMap);

        if (!requestMapAccess(resolvedMap)) {
            return;
        }

        sceneDetailRequestRef.current += 1;
        setSelectedLocationId(null);
        setOpenLocationDetail(false);
        setCurrentLocation(null);
        setShowMapPopup(true);
        onOpenChange?.(true);
    }, [externalOpenSignal, displayMaps, getPreferredMap, requestMapAccess, onOpenChange, resolveExistingMap]);

    const handleBackLocationDetail = () => {
        sceneDetailRequestRef.current += 1;
        setOpenLocationDetail(false);
        setCurrentLocation(null);
        setSelectedLocationId(null);
        setOpenListMarkers(false);
    };

    const handleGoToLocation = (sceneName: string) => {
        sceneDetailRequestRef.current += 1;
        setOpenLocationDetail(false);
        setCurrentLocation(null);
        setSelectedLocationId(null);
        setShowMapPopup(false);
        navigateScene("to", sceneName);
    };

    const handleOpenCurrenLocationDetail = () => {
        sceneDetailRequestRef.current += 1;
        setSceneDetailError(null);
        setOpenLocationDetail(true);
        const matchedLocation = mapLocations?.find(
            (item) => (item.type === 'scene' || item.type === 'view') && isLocationMatchingScene(item, currentScene)
        );
        if (matchedLocation) {
            setSelectedLocationId(matchedLocation.id);
            setCurrentLocation(matchedLocation);
        } else {
            setCurrentLocation(window?.currentSceneInfo);
        }
    };

    const handleOpenLocationDetail = (data: any) => {
        setOpenLocationDetail(true);
        setOpenListMarkers(false);
        setCurrentLocation(data);
        if (data?.id) {
            const matched = mapLocations?.find(
                (loc) => ((loc.type === 'scene' || loc.type === 'view') && String(loc.entity_id) === String(data.id)) ||
                         (data.name && isLocationMatchingScene(loc, data.name)) ||
                         (data.scene_id && isLocationMatchingScene(loc, data.scene_id))
            );
            if (matched) {
                setSelectedLocationId(matched.id);
            }
        } else if (data?.name || data?.scene_id) {
            const targetRef = data?.name || data?.scene_id;
            const matched = mapLocations?.find((loc) => (loc.type === 'scene' || loc.type === 'view') && isLocationMatchingScene(loc, targetRef));
            if (matched) {
                setSelectedLocationId(matched.id);
            }
        }
    };

    const handleMapSelect = (map: any) => {
        const publicMap = displayMaps.find((item: any) => String(item.id) === String(map?.id));
        if (!publicMap) {
            return;
        }
        if (!requestMapAccess(publicMap)) {
            return;
        }

        setMapSelected(publicMap);
    };

    const extractImage2dUrls = useCallback((target: any): string[] => {
        const config = target?.config;
        const fallback = [
            String(target?.entity_id || '').trim(),
            String(target?.code || '').trim(),
        ].filter((item) => isLikelyImageUrl(item));

        return dedupeImageUrls([
            ...extractImageUrlsDeep(config),
            ...fallback,
        ]);
    }, [dedupeImageUrls, extractImageUrlsDeep, isLikelyImageUrl]);

    const handleImage2dMarkerClick = useCallback((payload: { urls: string[]; title?: string }) => {
        openImage2dPreview(payload.urls || [], payload.title || 'Hinh anh 2D');
    }, [openImage2dPreview]);

    const handleLoginSuccess = useCallback((authData: { accessToken: string; id_path: string; user: any }) => {
        // Store the project auth locally — no NextAuth cookies involved,
        // so iOS Safari won't trigger ITP-related reloads.
        setProjectAuth(authData);

        // Broadcast auth to other MinimapPopupQTSC instances (same tab)
        // so they don't require a separate login
        if (typeof window !== 'undefined') {
            try {
                window.sessionStorage.setItem(projectAuthStorageKey, JSON.stringify(authData));
            } catch (error) {
                console.warn('Failed to persist minimap auth immediately after login:', error);
            }

            window.dispatchEvent(new CustomEvent('minimap-auth-sync', {
                detail: { key: projectAuthStorageKey, auth: authData }
            }));
        }

        setShowLoginDialog(false);
        const selectedAfterLogin = resolveExistingMap(pendingMap || mapSelected, displayMaps);
        if (selectedAfterLogin) {
            setMapSelected(selectedAfterLogin);
        } else {
            console.warn('Selected minimap no longer exists in current project data.');
            setPendingMap(null);
            return;
        }
        setPendingMap(null);

        if (isIPhoneSafari) {
            // On iPhone Safari we must ensure the panorama canvas is fully hidden
            // before the minimap WebGL context is created. Two simultaneous WebGL
            // contexts exhaust the GPU memory budget and cause iOS to kill the tab.
            //
            // Step 1 (immediate): hide the panorama so the GPU compositor drops it.
            const panoEl = document.getElementById('pano');
            if (panoEl) {
                panoEl.style.visibility = 'hidden';
                panoEl.style.willChange = 'auto';
                const canvas = panoEl.querySelector('canvas') as HTMLCanvasElement | null;
                if (canvas) {
                    canvas.style.visibility = 'hidden';
                    canvas.style.transform = 'none';
                }
            }
            // Step 2 (delayed): open minimap AFTER Safari has had time to finish
            // keyboard dismiss + layout + GPU layer eviction (~400ms on real devices).
            window.setTimeout(() => {
                setShowMapPopup(true);
                onOpenChange?.(true);
            }, 400);
        } else {
            // Non-Safari: open immediately (no WebGL conflict).
            setShowMapPopup(true);
            onOpenChange?.(true);
        }
    }, [pendingMap, mapSelected, isIPhoneSafari, onOpenChange, projectAuthStorageKey, resolveExistingMap, displayMaps]);

    const handleCloseLoginDialog = useCallback(() => {
        setPendingMap(null);
        setShowLoginDialog(false);
    }, []);

    const findMapSelect = (map_id: any) => {
        const findMap = displayMaps.find((m: any) => String(m.id) === String(map_id));
        if (!findMap) {
            return;
        }
        if (!requestMapAccess(findMap)) {
            return;
        }
        setMapSelected(findMap);
    };

    const getSceneBySceneId = async (scene_name: string) => {
        try {
            const response = await sceneServices.getSceneBySceneName({
                id_path: idPath,
                language: locale,
                scene_name: scene_name,
                gender: "nu"
            });
            if (!response.error) {
                return response.data;
            }
        } catch (err) {
            console.error('Error fetching scene by name:', err);
        }
    };

    const getSceneByNumericId = async (scene_id: string) => {
        try {
            const response = await sceneServices.getSceneById({
                id_path: idPath,
                language: locale,
                scene_id: scene_id,
                gender: "nu"
            });
            if (!response.error) {
                return response.data;
            }
        } catch (err) {
            console.error('Error fetching scene by id:', err);
        }
    };

    const handleCustomSceneClick = async (sceneId: string, locationOrId?: LocationItem | string | number) => {
        const location = typeof locationOrId === 'object' ? locationOrId
            : locationOrId !== undefined ? mapLocations.find((loc) => String(loc.id) === String(locationOrId)) : undefined;
        const requestId = ++sceneDetailRequestRef.current;
        setSceneDetailError(null);
        setOpenLocationDetail(false);
        setCurrentLocation(null);
        const matched = location || mapLocations?.find((loc) =>
            (loc.type === 'scene' || loc.type === 'view') &&
            (isLocationMatchingScene(loc, sceneId) || String(loc.entity_id) === String(sceneId))
        );
        if (matched) {
            setSelectedLocationId(matched.id);
        }
        const numericId = String(matched?.entity_id ?? sceneId ?? '');
        const data = /^\d+$/.test(numericId)
            ? await getSceneByNumericId(numericId)
            : await getSceneBySceneId(matched?.ref || sceneId);
        if (requestId !== sceneDetailRequestRef.current) return;
        if (data) {
            handleOpenLocationDetail(data);
            if (matched) setSelectedLocationId(matched.id);
        } else {
            setSceneDetailError(t('LOCATION_DETAIL_ERROR') || 'Không tải được thông tin địa điểm. Vui lòng thử lại.');
            setOpenListMarkers(true);
        }
    };

    // A list row has one activation owner, shared with marker scene clicks.
    const handleLocationSelect = (location: LocationItem) => {
        sceneDetailRequestRef.current += 1;
        setSceneDetailError(null);
        setSelectedLocationId(location.id);
        if (location.type === 'scene' || location.type === 'view') {
            void handleCustomSceneClick(String(location.ref || location.entity_id || ''), location);
        } else if (location.type === 'map') {
            findMapSelect(location.entity_id);
        } else if (location.type === 'link') {
            window.open(location.entity_id, '_blank', 'noopener,noreferrer');
        } else if (location.type === 'image2d') {
            window.changeViewByMarkerHandlers?.minimap_popup_fixed?.(String(location.id), 'image2d');
        }
        if (window.innerWidth < 1024) setOpenListMarkers(false);
    };

    useEffect(() => {
        sceneDetailRequestRef.current += 1;
        setSceneDetailError(null);
        setOpenLocationDetail(false);
        setCurrentLocation(null);
        setSelectedLocationId(null);
        return () => { sceneDetailRequestRef.current += 1; };
    }, [mapSelected?.id, showMapPopup]);

    useEffect(() => {
        if (!showMapPopup) {
            setMapLocations([]);
            setHasCurrentSceneMarker(false);
            setCurrentLocation(null);
            setSelectedLocationId(null);
            setOpenLocationDetail(false);
            setOpenListMarkers(false);
        }
    }, [showMapPopup])

    // Lock page scroll while minimap overlays are open to avoid
    // external UI scrollbar/rubber-band artifacts on mobile Safari.
    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        const shouldLockPageScroll = showMapPopup || showLoginDialog;
        if (!shouldLockPageScroll) {
            return;
        }

        const htmlEl = document.documentElement;
        const bodyEl = document.body;
        const prevHtmlOverflow = htmlEl.style.overflow;
        const prevHtmlOverscroll = htmlEl.style.overscrollBehavior;
        const prevBodyOverflow = bodyEl.style.overflow;
        const prevBodyOverscroll = bodyEl.style.overscrollBehavior;

        htmlEl.style.overflow = 'hidden';
        htmlEl.style.overscrollBehavior = 'none';
        bodyEl.style.overflow = 'hidden';
        bodyEl.style.overscrollBehavior = 'none';

        return () => {
            htmlEl.style.overflow = prevHtmlOverflow;
            htmlEl.style.overscrollBehavior = prevHtmlOverscroll;
            bodyEl.style.overflow = prevBodyOverflow;
            bodyEl.style.overscrollBehavior = prevBodyOverscroll;
        };
    }, [showMapPopup, showLoginDialog]);

    // Safari on iPhone can only handle ONE heavy WebGL context at a time.
    // The main KRPano panorama (#pano) and the minimap KrpanoMapViewer both use WebGL,
    // and running both simultaneously causes iOS WebKit to kill the process.
    // Fix: when the minimap popup is open on iPhone Safari, visually hide the
    // panorama container so Safari can swap its GPU allocation to the minimap.
    // The panorama canvas is hidden (not unmounted), so it resumes instantly on close.
    useEffect(() => {
        if (!isIPhoneSafari) return;

        const panoEl = document.getElementById('pano');
        if (!panoEl) return;

        if (showMapPopup) {
            // Suspend panorama GPU memory. visibility:hidden keeps the DOM intact
            // (so KRPano doesn't reinitialize) but tells the GPU compositor to
            // discard the layer, freeing ~200-400MB for the minimap context.
            panoEl.style.visibility = 'hidden';
            panoEl.style.willChange = 'auto';
            // Also hint Safari to deprioritise canvas compositing
            const canvas = panoEl.querySelector('canvas') as HTMLCanvasElement | null;
            if (canvas) {
                canvas.style.visibility = 'hidden';
                // Remove any transform that forces GPU composite layer
                canvas.style.transform = 'none';
            }
        } else {
            // Restore panorama when minimap closes
            panoEl.style.visibility = '';
            panoEl.style.willChange = '';
            const canvas = panoEl.querySelector('canvas') as HTMLCanvasElement | null;
            if (canvas) {
                canvas.style.visibility = '';
                canvas.style.transform = '';
            }
        }

        return () => {
            // Safety cleanup: always restore on unmount
            panoEl.style.visibility = '';
            panoEl.style.willChange = '';
        };
    }, [showMapPopup, isIPhoneSafari]);

    // Also suspend panorama during the login dialog phase on iPhone Safari.
    // When the keyboard appears for text input, Safari resizes the viewport and
    // forces the panorama canvas to re-composite — this alone can spike memory
    // enough to trigger a kill before the map even opens.
    useEffect(() => {
        if (!isIPhoneSafari) return;

        const panoEl = document.getElementById('pano');
        if (!panoEl) return;

        if (showLoginDialog) {
            panoEl.style.visibility = 'hidden';
            panoEl.style.willChange = 'auto';
        } else if (!showMapPopup) {
            // Only restore if map popup is also closed; otherwise the map popup
            // effect will handle the restore when it closes.
            panoEl.style.visibility = '';
            panoEl.style.willChange = '';
        }
    }, [showLoginDialog, showMapPopup, isIPhoneSafari]);

    // iOS Safari touch event fix: Prevent pull-to-refresh and swipe navigation
    // Must use native addEventListener with { passive: false } because React's
    // synthetic onTouchMove is passive by default and ignores preventDefault()
    useEffect(() => {
        const el = overlayRef.current;
        if (!el || !showMapPopup) return;

        // Only prevent touchmove to stop pull-to-refresh/swipe navigation
        // Do NOT block touchstart as it breaks click synthesis for buttons
        const handleTouchMove = (e: TouchEvent) => {
            const target = e.target as HTMLElement;
            // Allow normal scrolling in scrollable areas
            const isScrollableContent = target.closest('.overflow-y-auto, .overflow-x-auto');
            // Allow touch on interactive elements (buttons, clickable divs)
            const isInteractiveElement = target.closest('button, [role="button"], .cursor-pointer, .pointer-events-auto');

            if (!isScrollableContent && !isInteractiveElement) {
                e.preventDefault();
            }
            // Do NOT call stopPropagation - it blocks events from reaching child elements
        };

        el.addEventListener('touchmove', handleTouchMove, { passive: false, capture: false });

        return () => {
            el.removeEventListener('touchmove', handleTouchMove, { capture: false });
        };
    }, [showMapPopup]);

    return (
        <>
            <LoginDialog
                open={showLoginDialog}
                idPath={idPath}
                mapTitle={pendingMap?.title}
                onClose={handleCloseLoginDialog}
                onSuccess={handleLoginSuccess}
            />

            {/* Map popup overlay */}
            {showMapPopup && mapSelected?.id ? (
                <div
                    ref={overlayRef}
                    className={`fixed top-0 left-0 right-0 bottom-0 flex z-80 items-center justify-center p-4 transition-[opacity,backdrop-filter] duration-300 ${isClosing ? 'animate-fadeOut bg-black/0 backdrop-blur-none' : 'animate-fadeIn bg-black/10'}`}
                    style={{ touchAction: 'none', overscrollBehavior: 'none' }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            closeOverlay();
                        }
                    }}
                >
                    <div
                        style={{ height: '95dvh', overscrollBehavior: 'contain', touchAction: 'pan-y' }}
                        className={`w-full sm:max-w-[500px] lg:max-w-[80vw] bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col ${isClosing ? 'animate-scaleOut' : 'animate-scaleIn'}`}
                    >
                        {/* Header */}
                        <div className="z-100 bg-white p-3 border-b border-gray-200 shadow-sm flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 pl-2">
                                    <IconMinimap style={{ width: '24px', height: '24px' }} stroke="black" />
                                    <h2 className="text-black font-bold text-xl md:text-2xl">{t('MAP_TITLE')}</h2>
                                </div>
                                <button
                                    onClick={closeOverlay}
                                    className="bg-[var(--button-exit-bg)] p-2 rounded-lg flex items-center justify-center transition-opacity hover:opacity-80 cursor-pointer"
                                    style={{ touchAction: 'manipulation' }}
                                    aria-label={t('CLOSE') || 'Đóng'}
                                >
                                    <IconX style={{ width: '20px', height: '20px' }} stroke="var(--button-exit-label)" />
                                </button>
                            </div>
                        </div>

                        <div className="flex lg:flex items-center flex-1 min-h-0 overflow-hidden">
                            {/* Map Area */}
                            <div className={`flex-grow flex overflow-hidden h-full relative ${mapSelected?.type === '3d' ? 'w-full lg:w-full' : 'lg:w-[70%]'}`}>

                                {/* Map Title */}
                                {mapSelected?.type !== '3d' && (
                                    <span style={{ boxShadow: '0px 4px 4px 0px #00000040, 0px -2px 2px 0px #00000040 inset', textTransform: 'uppercase' }} className="rounded-2xl absolute top-3 left-1/2 -translate-x-1/2 min-w-[100px] py-2 px-3 z-100 font-semibold bg-[var(--button-secondary-bg-default)] text-[var(--button-secondary-label-default)] cursor-pointer text-[14px] text-center">
                                        {selectedMapTitle || t('POPUP_MAP_TITLE') || t('MAP_TITLE') || 'Digital Map'}
                                    </span>
                                )}

                                <div className="flex-1 w-full h-full bg-gray-100 relative">
                                    {mapSelected?.type === '3d' ? (
                                        <iframe
                                            src={getEmbedUrl(mapSelected?.url_map)}
                                            className="w-full h-full border-0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    ) : (
                                        <>
                                            <KrpanoMapViewer
                                                mapId={mapSelected?.id?.toString()}
                                                mapMarkerDisplay={mapSelected?.marker_display}
                                                targetElementId={`minimap_popup_fixed`}
                                                accessToken={Number(mapSelected?.require_login) === 1 ? projectAuth?.accessToken : undefined}
                                                forceExpanded={true}
                                                showBackButton={true}
                                                autoInitialize={true}
                                                onMarkerClick={handleMarkerClick}
                                                onImage2DMarkerClick={handleImage2dMarkerClick}
                                                onMapReady={handleMapReady}
                                                onLocationsLoaded={handleLocationsLoaded}
                                                customSceneClickHandler={handleCustomSceneClick}
                                                customGroupClickHandler={findMapSelect}
                                                showPolygons={true}
                                                showZoomControls={false}
                                                onZoomControlsReady={(controls) => {
                                                    mapControlsRef.current = controls;
                                                }}
                                                hoveredLocationId={hoveredLocationId}
                                                onMarkerHover={setHoveredLocationId}
                                                currentScene={currentScene}
                                                selectedLocationId={selectedLocationId}
                                                onMarkerSelect={(locationId) => {
                                                    sceneDetailRequestRef.current += 1;
                                                    setSceneDetailError(null);
                                                    setSelectedLocationId(locationId);
                                                }}
                                                onPolygonClick={async (polygon) => {
                                                    // Handle polygon click - navigate to scene if polygon is linked to a scene
                                                    if (polygon.type === 'scene' && polygon.entity_id) {
                                                        try {
                                                            // entity_id is a numeric scene ID → use getSceneById
                                                            const sceneData = await getSceneByNumericId(polygon.entity_id.toString());
                                                            if (sceneData?.name) {
                                                                // Navigate directly to the scene
                                                                closeOverlay();
                                                                navigateScene("to", sceneData.name);
                                                            } else if (sceneData) {
                                                                handleOpenLocationDetail(sceneData);
                                                            }
                                                        } catch (err) {
                                                            console.error('Error handling polygon click:', err);
                                                        }
                                                    } else if (polygon.type === 'map' && polygon.entity_id) {
                                                        findMapSelect(polygon.entity_id);
                                                    } else if (polygon.type === 'image2d') {
                                                        const imageUrls = extractImage2dUrls(polygon);
                                                        if (imageUrls.length === 0) {
                                                            console.warn('image2d polygon clicked but no image URL found:', polygon);
                                                            return;
                                                        }

                                                        openImage2dPreview(imageUrls, polygon?.title || 'Hinh anh 2D');
                                                    }
                                                }}
                                                className="w-full h-full"
                                            />

                                            {/* External zoom controls rendered above all map layers */}
                                            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 3000 }}>
                                                <div
                                                    className="absolute bottom-4 right-4 flex flex-col gap-2 pointer-events-auto"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onPointerUp={(e) => e.stopPropagation()}
                                                    onWheel={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => mapControlsRef.current?.zoomIn()}
                                                        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 active:scale-95 transition-transform cursor-pointer"
                                                        style={{ touchAction: 'manipulation' }}
                                                        title={t('ZOOM_IN') || 'Phóng to'}
                                                        aria-label={t('ZOOM_IN') || 'Phóng to'}
                                                    >
                                                        <Plus className="w-5 h-5 text-gray-700" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => mapControlsRef.current?.zoomOut()}
                                                        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 active:scale-95 transition-transform cursor-pointer"
                                                        style={{ touchAction: 'manipulation' }}
                                                        title={t('ZOOM_OUT') || 'Thu nhỏ'}
                                                        aria-label={t('ZOOM_OUT') || 'Thu nhỏ'}
                                                    >
                                                        <Minus className="w-5 h-5 text-gray-700" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => mapControlsRef.current?.resetZoom()}
                                                        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 active:scale-95 transition-transform cursor-pointer"
                                                        style={{ touchAction: 'manipulation' }}
                                                        title={t('RESET_ZOOM') || 'Đặt lại zoom'}
                                                        aria-label={t('RESET_ZOOM') || 'Đặt lại zoom'}
                                                    >
                                                        <RotateCcw className="w-5 h-5 text-gray-700" />
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* List Location */}
                                {mapSelected?.type !== '3d' && (
                                    <div className="absolute bottom-[65px] lg:bottom-[15px] left-[15px] z-100 gap-3 overflow-x-auto pointer-events-none" style={{ width: 'calc(100% - 30px)' }}>
                                        {
                                            hasCurrentSceneMarker && (
                                                <div onClick={() => handleOpenCurrenLocationDetail()} className="flex items-center justify-center w-[45px] lg:w-fit lg:px-3 rounded-xl h-[45px] bg-[var(--gen-bg-primary)] mb-4 cursor-pointer shadow-lg pointer-events-auto">
                                                    <IconCurrentLocation style={{ width: '20px', height: '20px' }} />
                                                    {openLocationDetail && <span className="text-[#38C348] text-[12px] ml-2 font-semibold hidden lg:block">{t('GROUPVIEW_POSITION')}</span>}
                                                </div>
                                            )
                                        }
                                        <div className="pointer-events-auto">
                                                <ListMap maps={displayMaps} onMapSelect={handleMapSelect} currentMapId={mapSelected?.id} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Menu location */}
                            {mapSelected?.type !== '3d' && (
                                <div
                                    className={`
                                    absolute bottom-0 left-0 lg:static z-[1000]
                                    transition-[transform] duration-300 ease-in-out shadow-lg
                                    ${openListMarkers ? 'translate-y-0' : 'translate-y-[100%] lg:translate-y-0'}
                                    w-full lg:w-[30%]
                                    max-h-[75vh] lg:max-h-full
                                    lg:h-full overflow-hidden
                                `}
                                    style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
                                >

                                    {sceneDetailError && <p role="alert" className="bg-red-50 p-3 text-sm text-red-700">{sceneDetailError}</p>}
                                    <ListLocation
                                        minimapId={mapSelected?.id}
                                        mapMarkerDisplay={mapSelected?.marker_display}
                                        mapLocations={mapLocations}
                                        currentScene={currentScene}
                                        onLocationSelect={handleLocationSelect}
                                        onClose={() => handleBackLocationDetail()}
                                        openCurrentLocationDetail={window?.innerWidth >= 1024 ? openLocationDetail : false}
                                        setOpenCurrentLocationDetail={(open) => {
                                            if (!open) sceneDetailRequestRef.current += 1;
                                            setOpenLocationDetail(open);
                                        }}
                                        handleOpenLocationDetail={handleOpenLocationDetail}
                                        handleGoToLocation={handleGoToLocation}
                                        currentLocation={currentLocation}
                                        findMapSelect={findMapSelect}
                                        hoveredLocationId={hoveredLocationId}
                                        onLocationHover={setHoveredLocationId}
                                        selectedLocationId={selectedLocationId}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Button List Location */}
                        {mapSelected?.type !== '3d' && (
                            <div className={`absolute bottom-0 left-0 w-full z-100 lg:hidden ${openLocationDetail ? "" : "border-t border-[var(--gen-outline)]"}`}>
                                {
                                    openLocationDetail && (
                                        <div style={{ margin: '0 auto 10px', touchAction: 'manipulation' }} onClick={() => handleBackLocationDetail()} className="cursor-pointer w-[44px] h-[44px] lg:hidden flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.35)] rounded-full bg-[#E34935] active:scale-95 transition-transform duration-200 border-2 border-white/95" title={t('CLOSE') || 'Đóng'}>
                                            <IconX style={{ width: '20px', height: '20px' }} stroke="#FFFFFF" />
                                        </div>
                                    )
                                }
                                <div className="flex items-center gap-3 px-4 h-[50px] bg-[var(--button-primary-bg-default)] cursor-pointer" style={{ touchAction: 'manipulation' }} onClick={() => setOpenListMarkers(true)}>
                                    {
                                        currentLocation?.name || currentLocation?.title ? (
                                            <IconArrowLeft style={{ width: '20px', height: '20px' }} stroke="var(--button-primary-label-default)" />
                                        ) : (
                                            <IconBulletList style={{ width: '20px', height: '20px' }} stroke="var(--button-primary-label-default)" />
                                        )
                                    }
                                    <h2 className="text-[var(--button-primary-label-default)] font-bold text-[16px] md:text-[18px]">{currentLocation?.name || currentLocation?.title ? t('BACK_TO_LIST') : t('LIST_LOCATION')}</h2>
                                </div>

                                {/* UI List Location */}
                                {
                                    openLocationDetail && (
                                        <LocationDetail
                                            location={currentLocation}
                                            onBack={handleBackLocationDetail}
                                            onGoToLocation={handleGoToLocation}
                                            className={`${openLocationDetail ? 'h-fit' : 'h-[0px]'}`}
                                        />
                                    )
                                }
                            </div>
                        )}

                        {image2dPreview.open && (
                            <div
                                className="absolute inset-0 z-[4000] bg-black/80 flex items-center justify-center p-4"
                                onClick={() => setImage2dPreview({ open: false, urls: [], activeUrl: '', title: '' })}
                            >
                                <div
                                    className="relative w-[95vw] sm:w-full max-w-[980px] bg-white rounded-xl overflow-hidden flex flex-col max-h-[95vh] h-[95vh]"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="flex items-center justify-between p-3 border-b border-gray-200 shrink-0">
                                        <h3 className="text-sm md:text-base font-semibold text-gray-800 line-clamp-1 pr-2">{image2dPreview.title}</h3>
                                        <button
                                            type="button"
                                            onClick={() => setImage2dPreview({ open: false, urls: [], activeUrl: '', title: '' })}
                                            className="bg-[var(--button-exit-bg)] p-2 rounded-lg hover:opacity-80 cursor-pointer shrink-0"
                                            aria-label="Dong"
                                        >
                                            <IconX style={{ width: '18px', height: '18px' }} stroke="var(--button-exit-label)" />
                                        </button>
                                    </div>

                                    <div className="p-3 bg-black/5 flex flex-col gap-3 overflow-hidden flex-1 min-h-0">
                                        <div className="relative w-full rounded-md bg-black overflow-hidden flex-[1_1_0] min-h-[30vh]">
                                            <div className="absolute top-2 left-2 z-20 text-[11px] px-2 py-1 rounded bg-black/60 text-white">
                                                {`${Math.round(previewScale * 100)}%`}
                                            </div>
                                            <div className="absolute top-2 right-2 z-20 flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={zoomInPreview}
                                                    className="p-1.5 rounded bg-white/95 hover:bg-white shadow"
                                                    aria-label="Phong to anh"
                                                >
                                                    <Plus className="w-4 h-4 text-gray-700" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={zoomOutPreview}
                                                    className="p-1.5 rounded bg-white/95 hover:bg-white shadow"
                                                    aria-label="Thu nho anh"
                                                >
                                                    <Minus className="w-4 h-4 text-gray-700" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={resetPreviewTransform}
                                                    className="p-1.5 rounded bg-white/95 hover:bg-white shadow"
                                                    aria-label="Dat lai ti le"
                                                >
                                                    <RotateCcw className="w-4 h-4 text-gray-700" />
                                                </button>
                                            </div>

                                            <div
                                                ref={previewViewportRef}
                                                className="w-full h-full flex items-center justify-center touch-none"
                                                onWheel={handlePreviewWheel}
                                                onMouseDown={(event) => startPreviewDrag(event.clientX, event.clientY)}
                                                onMouseMove={(event) => movePreviewDrag(event.clientX, event.clientY)}
                                                onMouseUp={stopPreviewDrag}
                                                onMouseLeave={stopPreviewDrag}
                                                onTouchStart={(event) => {
                                                    if (event.touches.length === 2) {
                                                        previewPinchDistanceRef.current = getTouchDistance(event.touches[0], event.touches[1]);
                                                        previewDragRef.current.active = false;
                                                    } else if (event.touches.length === 1) {
                                                        startPreviewDrag(event.touches[0].clientX, event.touches[0].clientY);
                                                    }
                                                }}
                                                onTouchMove={(event) => {
                                                    if (event.touches.length === 2) {
                                                        event.preventDefault();
                                                        const nextDistance = getTouchDistance(event.touches[0], event.touches[1]);
                                                        const previousDistance = previewPinchDistanceRef.current;

                                                        if (previousDistance && previousDistance > 0) {
                                                            const ratio = nextDistance / previousDistance;
                                                            setPreviewScale((prev) => clampPreviewScale(prev * ratio));
                                                        }

                                                        previewPinchDistanceRef.current = nextDistance;
                                                    } else if (event.touches.length === 1 && previewDragRef.current.active) {
                                                        event.preventDefault();
                                                        movePreviewDrag(event.touches[0].clientX, event.touches[0].clientY);
                                                    }
                                                }}
                                                onTouchEnd={(event) => {
                                                    if (event.touches.length === 0) {
                                                        stopPreviewDrag();
                                                        previewPinchDistanceRef.current = null;
                                                    }
                                                }}
                                            >
                                                <div
                                                    className="relative w-full h-full"
                                                    style={{
                                                        transform: `translate(${previewTranslate.x}px, ${previewTranslate.y}px) scale(${previewScale})`,
                                                        transformOrigin: 'center center',
                                                        transition: previewDragRef.current.active ? 'none' : 'transform 0.12s ease-out',
                                                        cursor: previewScale > 1 ? (previewDragRef.current.active ? 'grabbing' : 'grab') : 'default',
                                                    }}
                                                >
                                                    <Image
                                                        src={image2dPreview.activeUrl}
                                                        alt={image2dPreview.title}
                                                        fill
                                                        className="object-contain select-none"
                                                        unoptimized
                                                        draggable={false}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {image2dPreview.urls.length > 1 && (
                                            <div className="shrink-0 flex flex-col gap-2">
                                                <p className="text-xs text-gray-600">
                                                    {`${t('IMAGE') || 'Ảnh'} ${image2dPreview.urls.findIndex((url) => url === image2dPreview.activeUrl) + 1}/${image2dPreview.urls.length}`}
                                                </p>
                                                <div className="overflow-x-auto pb-1">
                                                    <div className="flex gap-2 min-w-max">
                                                        {image2dPreview.urls.map((url, index) => (
                                                            <button
                                                                key={`${url}-${index}`}
                                                                type="button"
                                                                onClick={() => setImage2dPreview((prev) => ({ ...prev, activeUrl: url }))}
                                                                className={`relative rounded-md overflow-hidden border-2 transition-all w-20 h-14 sm:w-24 sm:h-16 ${image2dPreview.activeUrl === url ? 'border-[#E34935]' : 'border-transparent hover:border-gray-300'}`}
                                                            >
                                                                <Image
                                                                    src={url}
                                                                    alt={`${image2dPreview.title} ${index + 1}`}
                                                                    fill
                                                                    className="object-cover"
                                                                    unoptimized
                                                                />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                !showLoginDialog && !hideToggleButton && displayMaps.length > 0 ? (
                    <ToggleMapButton
                        handleShowMapPopup={handleShowMapPopup}
                        dataMap={displayMaps}
                    />
                ) : null
            )}
        </>
    );
}
